import { useEffect, useState } from 'react'

import { utils } from '@coral-xyz/anchor'
import { getAssociatedTokenAddress } from '@solana/spl-token'
import { PublicKey } from '@solana/web3.js'
import { useQuery } from '@tanstack/react-query'

import { SOLANA_CONNECTION, SOLANA_USDC_ADDRESS } from '@src/chains/solana/config'
import { ChainId } from '@src/configs/chainInfo'
import { IToken } from '@src/types/token'
import { SolanaAddress } from '@src/types/token'
import { Balance } from '@src/utils/balance'
import { getTokenBalanceFromATA } from '@src/utils/solana/utils'
import { useTokensStore } from '@src/zustand_stores'

export interface FindProgramAddressResponse {
    publicKey: PublicKey
    bump: number
}

export const findProgramAddress = (
    label: string,
    programId: PublicKey,
    extraSeeds: (string | number[] | Buffer | PublicKey)[] = []
): FindProgramAddressResponse => {
    const seeds = [Buffer.from(utils.bytes.utf8.encode(label))]
    if (extraSeeds) {
        for (const extraSeed of extraSeeds) {
            if (typeof extraSeed === 'string') {
                seeds.push(Buffer.from(utils.bytes.utf8.encode(extraSeed)))
            } else if (Array.isArray(extraSeed)) {
                seeds.push(Buffer.from(extraSeed.map(Number)))
            } else if (Buffer.isBuffer(extraSeed)) {
                // @ts-ignore
                seeds.push(extraSeed)
            } else {
                // @ts-ignore
                seeds.push(extraSeed.toBuffer())
            }
        }
    }
    const res = PublicKey.findProgramAddressSync(seeds, programId)
    return { publicKey: res[0], bump: res[1] }
}

export const getSolanaTransactionReceipt = async (txHash: string) => {
    const receipt = await SOLANA_CONNECTION.getTransaction(txHash, {
        commitment: 'confirmed',
        maxSupportedTransactionVersion: 0,
    })

    if (!receipt) return null

    // Extract useful information from the receipt
    const transactionInfo = {
        slot: receipt.slot,
        blockTime: receipt.blockTime,
        // Transaction success status
        success: receipt.meta?.err === null,
        // Get logs from transaction
        logs: receipt.meta?.logMessages || [],
        // Get all token transfers
        postTokenBalances: receipt.meta?.postTokenBalances || [],
        preTokenBalances: receipt.meta?.preTokenBalances || [],
        // Get fee information
        fee: receipt.meta?.fee || 0,
        // Get inner instructions (for complex transactions)
        innerInstructions: receipt.meta?.innerInstructions || [],
        // Raw receipt for any other needed data
        raw: receipt,
    }

    return transactionInfo
}

export const getSolUSDCTokenAccount = async (publicKey: PublicKey) => {
    const associatedTokenAddress = await getAssociatedTokenAddress(
        SOLANA_USDC_ADDRESS, // Token mint address
        publicKey // Wallet address
    )
    return associatedTokenAddress
}

const useSolanaTokenBalance = (
    walletAddress: string,
    tokenMintAddress: string
): { balance: Balance | undefined; isLoading: boolean; refetch: () => Promise<void> } => {
    const { tokens: zustandTokens, isLoading: zustandTokensLoading } = useTokensStore()
    const [solanaToken, setSolanaToken] = useState<IToken[] | undefined>(undefined)

    useEffect(() => {
        if (zustandTokensLoading) return
        setSolanaToken(zustandTokens.filter((t: IToken) => t.chainId === ChainId.Solana))
    }, [zustandTokens, zustandTokensLoading])

    const fetchTokenBalance = async (): Promise<Balance | undefined> => {
        if (!walletAddress || !tokenMintAddress) {
            return undefined
        }

        if (tokenMintAddress === '0x0000000000000000000000000000000000000000') {
            const balance = await SOLANA_CONNECTION.getBalance(new PublicKey(walletAddress))

            return new Balance({
                decimals: 9,
                symbol: 'SOL',
                bigintValue: BigInt(balance.toString()),
                name: 'SOL',
                chainId: ChainId.Solana,
                address: '0x0000000000000000000000000000000000000000' as SolanaAddress,
            })
        }

        let tokenMintPublicKey: PublicKey

        try {
            tokenMintPublicKey = new PublicKey(tokenMintAddress)
            PublicKey.isOnCurve(tokenMintPublicKey) === true
        } catch {
            return undefined
        }

        const tokenAmount = await getTokenBalanceFromATA(walletAddress, tokenMintAddress)

        const token = solanaToken?.find(t => t.address === tokenMintAddress)
        if (!token) {
            return undefined
        }

        return new Balance({
            decimals: token.decimals,
            symbol: token.symbol,
            bigintValue: BigInt(tokenAmount * 10 ** token.decimals),
            name: token.name,
            chainId: ChainId.Solana,
            address: tokenMintAddress as SolanaAddress,
        })
    }

    const {
        data: balance,
        isLoading,
        refetch: queryRefetch,
    } = useQuery({
        queryKey: ['solana-token-balance', walletAddress, tokenMintAddress],
        queryFn: fetchTokenBalance,
        enabled: !!walletAddress && !!tokenMintAddress,
    })

    const refetch = async () => {
        await queryRefetch()
    }

    return { balance, isLoading, refetch }
}

export default useSolanaTokenBalance
